import React, { useState } from 'react';
import {
    FormatSize, ArrowDropDown, ArrowUpward, ArrowDownward,
    FormatColorText, Check
} from '@mui/icons-material';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import Popover from '@mui/material/Popover';
import { CustomIconButton } from './richTextEditor';
import { Extension } from '@tiptap/core'

export const TextSizeDropdown = ({ editor }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const fontSizes = [
        { label: 'X-Small', value: '12px' },
        { label: 'Small', value: '14px' },
        { label: 'Normal', value: '16px' },
        { label: 'Large', value: '18px' },
        { label: 'X-Large', value: '20px' },
        { label: 'XX-Large', value: '24px' },
    ];

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleFontSizeChange = (fontSize) => {
        editor.chain().focus().setFontSize(fontSize).run();
        handleClose();
    };

    const increaseFontSize = () => {
        const currentSelection = editor.state.selection;
        const currentAttrs = editor.getAttributes('textStyle');
        const currentSize = currentAttrs.fontSize || '16px';
        const currentIndex = fontSizes.findIndex(size => size.value === currentSize);

        if (currentIndex < fontSizes.length - 1) {
            editor.chain().focus().setFontSize(fontSizes[currentIndex + 1].value).run();
        }
    };

    const decreaseFontSize = () => {
        const currentAttrs = editor.getAttributes('textStyle');
        const currentSize = currentAttrs.fontSize || '16px';
        const currentIndex = fontSizes.findIndex(size => size.value === currentSize);

        if (currentIndex > 0) {
            editor.chain().focus().setFontSize(fontSizes[currentIndex - 1].value).run();
        }
    };

    const open = Boolean(anchorEl);
    const id = open ? 'font-size-popover' : undefined;

    return (
        <div className="flex items-center">
            <CustomIconButton
                IconComponent={TextIncreaseIcon}
                onClick={increaseFontSize}
                tooltip="Increase Size"
            />
            <CustomIconButton
                IconComponent={TextDecreaseIcon}
                onClick={decreaseFontSize}
                tooltip="Decrease Size"
            />
            <CustomIconButton
                IconComponent={FormatSize}
                onClick={handleClick}
                tooltip="Font Size"
            />
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <div className="p-2 w-36">
                    {fontSizes.map((size) => (
                        <div
                            key={size.value}
                            className="px-2 py-1 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                            onClick={() => handleFontSizeChange(size.value)}
                        >
                            <span style={{ fontSize: size.value }}>{size.label}</span>
                            {editor.getAttributes('textStyle').fontSize === size.value && (
                                <Check fontSize="small" />
                            )}
                        </div>
                    ))}
                </div>
            </Popover>
        </div>
    );
};

export const ColorPicker = ({ editor }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const colors = [
        { label: 'Black', value: '#000000' },
        { label: 'Red', value: '#FF0000' },
        { label: 'Blue', value: '#0000FF' },
        { label: 'Green', value: '#008000' },
        { label: 'Purple', value: '#800080' },
        { label: 'Orange', value: '#FFA500' },
        { label: 'Gray', value: '#808080' },
    ];

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleColorChange = (color) => {
        editor.chain().focus().setColor(color).run();
        handleClose();
    };

    const open = Boolean(anchorEl);
    const id = open ? 'color-picker-popover' : undefined;

    return (
        <div className="flex items-center">
            <CustomIconButton
                IconComponent={FormatColorText}
                onClick={handleClick}
                tooltip="Text Color"
            />
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <div className="p-2 grid grid-cols-4 gap-2">
                    {colors.map((color) => (
                        <div
                            key={color.value}
                            className="w-6 h-6 rounded cursor-pointer border border-gray-300 flex items-center justify-center"
                            style={{ backgroundColor: color.value }}
                            onClick={() => handleColorChange(color.value)}
                            title={color.label}
                        >
                            {editor.getAttributes('textStyle').color === color.value && (
                                <Check fontSize="small" style={{ color: color.value === '#000000' ? 'white' : 'black' }} />
                            )}
                        </div>
                    ))}
                </div>
            </Popover>
        </div>
    );
};

// Define a custom extension for font size
export const FontSize = Extension.create({
    name: 'fontSize',

    addOptions() {
        return {
            types: ['textStyle'],
        };
    },

    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: element => element.style.fontSize,
                        renderHTML: attributes => {
                            if (!attributes.fontSize) {
                                return {};
                            }
                            return {
                                style: `font-size: ${attributes.fontSize}`,
                            };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setFontSize: fontSize => ({ chain }) => {
                return chain().setMark('textStyle', { fontSize }).run();
            },
        };
    },
});

// Define a custom extension for text color
export const TextColor = Extension.create({
    name: 'textColor',

    addOptions() {
        return {
            types: ['textStyle'],
        };
    },

    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    color: {
                        default: null,
                        parseHTML: element => element.style.color,
                        renderHTML: attributes => {
                            if (!attributes.color) {
                                return {};
                            }
                            return {
                                style: `color: ${attributes.color}`,
                            };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setColor: color => ({ chain }) => {
                return chain().setMark('textStyle', { color }).run();
            },
        };
    },
});